<template>
    <form :action="action" method="GET" role="search" :class="variant === 'page' ? 'search-page-form' : 'header-search'" @submit="onSubmit">
        <div class="suggest-wrap">
            <input
                type="search"
                name="q"
                v-model="q"
                :placeholder="placeholder"
                autocomplete="off"
                aria-label="Search the site"
                @input="onInput"
                @keydown.down.prevent="move(1)"
                @keydown.up.prevent="move(-1)"
                @keydown.enter="onEnter"
                @keydown.esc="open = false"
                @focus="q.length >= 2 && suggestions.length && (open = true)"
                @blur="closeSoon"
            >
            <ul v-if="open && suggestions.length" class="suggest-list" role="listbox">
                <li v-for="(s, i) in suggestions" :key="s.url + s.label" role="option" :aria-selected="i === active">
                    <a :href="s.url" :class="{ active: i === active }" @mousedown.prevent="go(s)">
                        <span class="suggest-main">
                            <svg class="ico suggest-type" aria-hidden="true"><use :href="'#i-' + String(icons[s.type] || 'fa-magnifying-glass').slice(3)"></use></svg>
                            <span class="suggest-text">
                                {{ s.label }}
                                <small v-if="s.sublabel">{{ s.sublabel }}</small>
                            </span>
                        </span>
                        <strong v-if="s.fee">{{ s.fee }}</strong>
                    </a>
                </li>
            </ul>
        </div>
        <button type="submit" :class="{ btn: variant === 'page' }" :aria-label="variant === 'page' ? undefined : 'Search'">
            <template v-if="variant === 'page'">Search</template>
            <svg class="ico" v-else  aria-hidden="true"><use href="#i-magnifying-glass"></use></svg>
        </button>
    </form>
</template>

<script setup>
import { ref } from 'vue';
import { track } from '../analytics';

const props = defineProps({
    action: { type: String, required: true },
    initial: { type: String, default: '' },
    variant: { type: String, default: 'header' },
    placeholder: { type: String, default: 'Search routes, fees…' },
});

const q = ref(props.initial);
const suggestions = ref([]);
const open = ref(false);
const active = ref(-1);
let timer = null;
let lastQuery = '';

const icons = {
    route: 'fa-road',
    expressway: 'fa-map-location-dot',
    point: 'fa-location-dot',
    guide: 'fa-book-open',
    faq: 'fa-circle-question',
};

function onInput() {
    active.value = -1;
    clearTimeout(timer);
    const query = q.value.trim();
    if (query.length < 2) {
        suggestions.value = [];
        open.value = false;
        return;
    }
    timer = setTimeout(fetchSuggestions, 180);
}

async function fetchSuggestions() {
    const query = q.value.trim();
    if (query === lastQuery && suggestions.value.length) {
        open.value = true;
        return;
    }
    try {
        const res = await fetch(`/api/search/suggest?q=${encodeURIComponent(query)}`);
        if (!res.ok) return;
        const data = await res.json();
        // Ignore stale responses typed-past in the meantime.
        if (q.value.trim() !== query) return;
        lastQuery = query;
        suggestions.value = data;
        open.value = data.length > 0;

        // A typed query we have nothing for. Alongside the assistant misses,
        // this is the clearest list of pages the site is missing.
        if (!data.length) {
            track('search_no_results', { search_term: query, source: props.variant });
        }
    } catch { /* network hiccup — plain submit still works */ }
}

function move(dir) {
    if (!open.value || !suggestions.value.length) return;
    active.value = (active.value + dir + suggestions.value.length) % suggestions.value.length;
}

function onEnter(e) {
    if (open.value && active.value >= 0) {
        e.preventDefault();
        go(suggestions.value[active.value]);
    }
    // else: default form submit to /search
}

function onSubmit() {
    open.value = false;
    track('site_search', { search_term: q.value.trim(), source: props.variant, via: 'submit' });
}

function go(s) {
    open.value = false;
    track('search_suggestion_click', {
        search_term: q.value.trim(),
        suggestion_type: s.type,
        label: s.label,
        position: suggestions.value.indexOf(s) + 1,
    });
    // gtag transports over sendBeacon, so the event survives the navigation
    // that starts on the next line.
    window.location.href = s.url;
}

function closeSoon() {
    setTimeout(() => (open.value = false), 150);
}
</script>
